/**
 * Gesture Theme Definitions
 *
 * Shared between frontend and backend for consistent theming across:
 * - UI labels (buttons, tabs, descriptors)
 * - Gesture display badges
 * - Notification text
 * - Email templates
 */
export const gestureThemes = {
    default: {
        spaceName: 'Dancefloor',
        gestures: {
            smile: { label: 'Smile', past: 'smiled at' },
            nod: { label: 'Nod', past: 'nodded to' },
            speak_to: { label: 'Speak To', past: 'spoke to' },
            dance_with: { label: 'Boogie', past: 'boogied with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your dancefloor - enjoy it with others'
        },
        emptyStateAction: 'connect with'
    },
    moshpit: {
        spaceName: 'Mosh Pit',
        gestures: {
            smile: { label: 'Grin', past: 'grinned at' },
            nod: { label: 'Share a beer', past: 'shared a beer with', returnLabel: 'Cheers', returnPast: 'cheers\'d with' },
            speak_to: { label: 'Shout to', past: 'shouted to' },
            dance_with: { label: 'Mosh', past: 'moshed with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your mosh pit - throw down with others'
        },
        emptyStateAction: 'throw down with'
    },
    speakeasy: {
        spaceName: 'The Speakeasy',
        gestures: {
            smile: { label: 'Beam', past: 'beamed at' },
            nod: { label: 'Tip your hat', past: 'tipped their hat to', pastAsSender: 'tipped your hat to', returnLabel: 'Tip back', returnPast: 'tipped back to' },
            speak_to: { label: 'Chat with', past: 'chatted with' },
            dance_with: { label: 'Swing', past: 'swung with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your speakeasy - jam with others'
        },
        emptyStateAction: 'jam with'
    },
    frontleft: {
        spaceName: 'Front Left',
        gestures: {
            smile: { label: 'Hug', past: 'hugged' },
            nod: { label: 'Share a bump', past: 'shared a bump with', returnLabel: 'Laugh together', returnPast: 'laughed with' },
            speak_to: { label: 'Chat with', past: 'chatted with' },
            dance_with: { label: 'Stomp', past: 'stomped with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your spot - enjoy with others'
        },
        emptyStateAction: 'rave with'
    },
    vineyard: {
        spaceName: 'The Vineyard',
        gestures: {
            smile: { label: 'Tip your hat', past: 'tipped their hat to', pastAsSender: 'tipped your hat to', returnLabel: 'Tip back', returnPast: 'tipped back to' },
            nod: { label: 'Raise a glass', past: 'raised a glass to', returnLabel: 'Clink glasses', returnPast: 'clinked glasses with' },
            speak_to: { label: 'Yarn with', past: 'yarned with' },
            dance_with: { label: 'Sway', past: 'swayed with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your vineyard - enjoy with others'
        },
        emptyStateAction: 'sit a spell with'
    },
    dive: {
        spaceName: 'The Dive',
        gestures: {
            smile: { label: 'Bop your head', past: 'bopped their head at', pastAsSender: 'bopped your head at', returnLabel: 'Bop back', returnPast: 'bopped back at' },
            nod: { label: 'Have a drink', past: 'had a drink with', returnLabel: 'Cheers', returnPast: 'cheers\'d with' },
            speak_to: { label: 'Chat with', past: 'chatted with' },
            dance_with: { label: 'Rock', past: 'rocked with' }
        },
        descriptor: {
            viewingOthers: (name) => `Connect with ${name} in a shared space`,
            viewingOwn: 'Your dive - rock with others'
        },
        emptyStateAction: 'rock with'
    }
};
/**
 * Get a gesture theme by key, with fallback to default
 */
export function getGestureTheme(themeKey) {
    if (!themeKey || !(themeKey in gestureThemes)) {
        return gestureThemes.default;
    }
    return gestureThemes[themeKey];
}
/**
 * Validate if a theme key is valid
 */
export function isValidGestureTheme(themeKey) {
    return themeKey in gestureThemes;
}
